import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logActivity } from './useActivityLog';
import { useEffect, useRef } from 'react';

export const useRosters = () => {
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Real-time subscription for rosters + roster_entries
  useEffect(() => {
    const invalidate = () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['rosters'] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        queryClient.invalidateQueries({ queryKey: ['checkins'] });
      }, 500);
    };

    const channel = supabase
      .channel('rosters-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rosters' }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'roster_entries' }, invalidate)
      .subscribe();

    return () => {
      clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const { data: rosters = [], isLoading } = useQuery({
    queryKey: ['rosters'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rosters')
        .select(`
          *,
          roster_entries(
            *,
            client:clients(
              id, 
              first_name, 
              last_name, 
              room_id, 
              status,
              partner_id,
              children,
              is_head_of_household,
              current_room:rooms(id, room_number)
            ),
            room:rooms(id, room_number)
          )
        `)
        .order('roster_date', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  const createRoster = useMutation({
    mutationFn: async ({ name, rosterDate, siteId, shiftType, notes, clientIds }: {
      name: string;
      rosterDate: string;
      siteId?: string;
      shiftType?: string;
      notes?: string;
      clientIds: string[];
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create the roster
      const { data: roster, error: rosterError } = await supabase
        .from('rosters')
        .insert({
          name,
          roster_date: rosterDate,
          site_id: siteId,
          shift_type: shiftType || 'current',
          notes,
          created_by: user.id
        })
        .select()
        .single();

      if (rosterError) throw rosterError;

      // Create roster entries for selected clients
      if (clientIds.length > 0) {
        const { data: clients } = await supabase
          .from('clients')
          .select('id, room_id, status, pass_start, pass_end')
          .in('id', clientIds);

        const entries = clients?.map(client => ({
          roster_id: roster.id,
          client_id: client.id,
          room_id: client.room_id
        })) || [];

        const { error: entriesError } = await supabase
          .from('roster_entries')
          .insert(entries);

        if (entriesError) throw entriesError;

        // Auto-seed the active (non-completed) bed_count for this site with these clients
        if (siteId) {
          const { data: activeBedCount } = await supabase
            .from('bed_counts')
            .select('id')
            .eq('site_id', siteId)
            .neq('status', 'completed')
            .order('scheduled_for', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (activeBedCount?.id) {
            const { data: existing } = await supabase
              .from('bed_count_entries')
              .select('client_id')
              .eq('bed_count_id', activeBedCount.id);
            const existingIds = new Set((existing ?? []).map((e: any) => e.client_id));
            const now = new Date();
            const newEntries = (clients ?? [])
              .filter((c: any) => !existingIds.has(c.id))
              .map((c: any) => {
                const onPass =
                  c.pass_start && c.pass_end &&
                  new Date(c.pass_start) <= now && new Date(c.pass_end) >= now;
                return {
                  bed_count_id: activeBedCount.id,
                  client_id: c.id,
                  room_id: c.room_id,
                  expected_status: onPass ? 'on_pass' : (c.status ?? 'checked-in'),
                };
              });
            if (newEntries.length > 0) {
              await supabase.from('bed_count_entries').insert(newEntries);
            }
          }
        }
      }

      await logActivity({
        actionType: 'create_roster',
        actionDetails: { roster_id: roster.id, name },
        siteId
      });

      return roster;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
      toast.success('Roster created successfully');
    },
    onError: (error) => {
      console.error('Error creating roster:', error);
      toast.error('Failed to create roster');
    }
  });

  const updateRosterEntries = useMutation({
    mutationFn: async ({ rosterId, clientIds }: {
      rosterId: string;
      clientIds: string[];
    }) => {
      // Delete existing entries
      await supabase
        .from('roster_entries')
        .delete()
        .eq('roster_id', rosterId);

      // Insert new entries
      if (clientIds.length > 0) {
        const { data: clients } = await supabase
          .from('clients')
          .select('id, room_id')
          .in('id', clientIds);

        const entries = clients?.map(client => ({
          roster_id: rosterId,
          client_id: client.id,
          room_id: client.room_id
        })) || [];

        const { error } = await supabase
          .from('roster_entries')
          .insert(entries);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
      toast.success('Roster updated successfully');
    },
    onError: (error) => {
      console.error('Error updating roster:', error);
      toast.error('Failed to update roster');
    }
  });

  const deleteRoster = useMutation({
    mutationFn: async (rosterId: string) => {
      const { error } = await supabase
        .from('rosters')
        .delete()
        .eq('id', rosterId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rosters'] });
      toast.success('Roster deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting roster:', error);
      toast.error('Failed to delete roster');
    }
  });

  return {
    rosters,
    isLoading,
    createRoster: createRoster.mutate,
    updateRosterEntries: updateRosterEntries.mutate,
    deleteRoster: deleteRoster.mutate
  };
};
